import React, { useCallback, useState } from 'react';
import CallMergeOutlinedIcon from '@mui/icons-material/CallMergeOutlined';
import ArcaPdfStudioLayout from '../../components/arca/ArcaPdfStudioLayout';
import { appendUploadFiles, isPdfFile } from '../../components/arca/uploadItemUtils';
import { getToken } from '../../api/client';
import API_CONFIG from '../../config/api';

function baixarBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export default function ArcaPdfJuntarPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const addFiles = useCallback((files) => {
    setError('');
    const n = appendUploadFiles(files, setItems, { multiple: true, filter: isPdfFile });
    if (!n) setError('Selecione apenas arquivos PDF.');
  }, []);

  const handleMerge = async () => {
    setError('');
    if (items.length < 2) {
      setError('Adicione pelo menos 2 PDFs para juntar.');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      items.forEach((it) => formData.append('files', it.file, it.file.name));
      formData.append('order', JSON.stringify(items.map((_, i) => String(i))));

      const token = getToken();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30 * 60 * 1000);
      let res;
      try {
        res = await fetch(`${API_CONFIG.BACKEND_URL}/api/arca/pdf/merge`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Erro ao juntar PDFs (${res.status}).`);
      if (!data.jobId || !data.downloadPath) {
        throw new Error('Resposta inválida do servidor ao juntar PDFs.');
      }

      const dl = await fetch(`${API_CONFIG.BACKEND_URL}${data.downloadPath}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!dl.ok) {
        const errBody = await dl.json().catch(() => ({}));
        throw new Error(errBody.error || `Erro ao baixar PDF juntado (${dl.status}).`);
      }
      const blob = await dl.blob();
      baixarBlob(blob, data.filename || `juntado-${Date.now()}.pdf`);
    } catch (e) {
      console.error('[ArcaPdfJuntar]', e);
      let msg = e.message || 'Erro ao juntar PDFs.';
      if (e.name === 'AbortError') {
        msg = 'Tempo esgotado ao juntar os PDFs. Aguarde e tente novamente.';
      } else if (/Failed to fetch|NetworkError|Load failed|ERR_CONNECTION/i.test(msg)) {
        msg =
          'Falha de conexão ao juntar/baixar. Confirme o backend em :3001 e tente de novo (até 40 arquivos · 100 MB cada).';
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ArcaPdfStudioLayout
      title="Juntar PDF"
      info="Para mudar a ordem, arraste e solte os arquivos. Clique no thumbnail para visualizar. Use ↻ para girar cada PDF."
      limits={
        <>
          Limites: até <strong>40</strong> arquivos · <strong>100 MB</strong> cada. A junção não
          comprime — só baixa o PDF final.
        </>
      }
      error={error}
      onClearError={() => setError('')}
      onRotateError={setError}
      items={items}
      setItems={setItems}
      loading={loading}
      dropzone={{
        accept: 'application/pdf,.pdf',
        multiple: true,
        maxSizeMb: 100,
        onFilesAdd: addFiles,
        label: 'Arraste PDFs aqui ou clique para selecionar',
        hint: 'Máximo 100 MB por arquivo · até 40 arquivos. Clique no card para visualizar · botão ↻ para girar.',
      }}
      actionLabel="Juntar PDF"
      actionLoadingLabel="Juntando…"
      actionStartIcon={<CallMergeOutlinedIcon />}
      onAction={handleMerge}
      actionDisabled={items.length < 2}
    />
  );
}
